/**
 * @fileoverview Mock backend for the Capabilities page.
 * @description Registers, on this feature's mock adapter, the three authorization reads the matrix
 * is built from — GET /authorization/roles, GET /authorization/permissions and GET
 * /authorization/roles/{roleId}/permissions, paged and filtered by status like the real ones — plus
 * PATCH /authorization/role-permissions (Update Role Permissions, the bulk save). Answers in the
 * backend's envelopes and, for a rejected change, its error codes. The mock doesn't check the caller's own authority —
 * the route guard already limits the page to Super Admin. A no-op when VITE_ENABLE_MOCK_API is
 * false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { capabilitiesMockAdapter } from './capabilities-mock-adapter'
import { listPermissionDtos, listRoleDtos, listRolePermissionDtos } from './capabilities-mock-store'
import { applyRolePermissionUpdate } from './capabilities-mock-update'
import { fail, ok, okPage } from './mock-response.utils'

import type { AxiosRequestConfig } from 'axios'

const ROLE_PERMISSIONS_UPDATE_PATH = '/authorization/role-permissions'

interface ListQuery {
  status: string
  page: number
  limit: number
}

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20

/**
 * @description Reads a positive whole number from a query value, which axios may deliver as a
 * string; anything else (absent, 0, negative, fractional, not a number) is the default, so a bad
 * `page` or `limit` can never slice the wrong rows.
 * @param {unknown} value - The query value as sent.
 * @param {number} fallback - What to use when the value is not a positive whole number.
 * @returns {number} The number.
 */
function toPositiveInteger(value: unknown, fallback: number): number {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * @description Reads a list request's status filter (ACTIVE when absent, as the real endpoints
 * default) and paging (page 1 and 20 per page when absent or not a positive whole number).
 * @param {AxiosRequestConfig} config - The mock request.
 * @returns {ListQuery} The typed query.
 */
function readQuery(config: AxiosRequestConfig): ListQuery {
  const params = (config.params ?? {}) as Record<string, unknown>
  return {
    status: typeof params.status === 'string' ? params.status : 'ACTIVE',
    page: toPositiveInteger(params.page, DEFAULT_PAGE),
    limit: toPositiveInteger(params.limit, DEFAULT_LIMIT),
  }
}

/** Matches GET /authorization/roles/{roleId}/permissions, capturing the (encoded) role id. */
const ROLE_PERMISSIONS_PATH = /^\/authorization\/roles\/([^/]+)\/permissions$/

if (capabilitiesMockAdapter) {
  const adapter = capabilitiesMockAdapter

  adapter.onGet('/authorization/roles').reply((config) => {
    const { status, page, limit } = readQuery(config)
    return [200, okPage(status === 'ACTIVE' ? listRoleDtos() : [], { page, limit })]
  })

  adapter.onGet('/authorization/permissions').reply((config) => {
    const { status, page, limit } = readQuery(config)
    return [200, okPage(listPermissionDtos(status), { page, limit })]
  })

  adapter.onGet(ROLE_PERMISSIONS_PATH).reply((config) => {
    const roleId = decodeURIComponent(ROLE_PERMISSIONS_PATH.exec(config.url ?? '')?.[1] ?? '')
    const { status, page, limit } = readQuery(config)
    const mappings = listRolePermissionDtos(roleId, status)

    return mappings
      ? [200, okPage(mappings, { page, limit })]
      : fail(404, '2002', 'That role does not exist.')
  })

  adapter.onPatch(ROLE_PERMISSIONS_UPDATE_PATH).reply((config) => {
    const result = applyRolePermissionUpdate(JSON.parse(config.data as string))

    return result.ok ? [200, ok(result.body)] : fail(result.status, result.appCode, result.message)
  })
}
